'use client'

import React from 'react'
import Image from 'next/image'
import { CheckCircle, Languages } from 'lucide-react'

import { Card } from '@/components/ui/card'
import StepActions from '@/components/booking/step-actions'
import type { BookingData, BookingSetter } from '@/types/booking'
import { bookingDoctors } from '@/components/booking/booking-doctors'

interface StepDoctorChoiceProps {
  bookingData: BookingData
  setBookingData: BookingSetter
  onNext: () => void
  onPrevious: () => void
}

type BookingDoctor = (typeof bookingDoctors)[number]

const isPsychiatrist = (doctor: BookingDoctor) =>
  doctor.id === '1' || /meena/i.test(doctor.name)

const clinicianListClass = 'flex flex-wrap justify-center gap-3 sm:gap-4'

const clinicianCardWidthClass =
  'w-[calc(50%_-_0.375rem)] sm:w-[calc(50%_-_0.5rem)] md:w-[calc(33.333%_-_0.667rem)] lg:w-[calc(25%_-_0.75rem)]'

const StepDoctorChoice: React.FC<StepDoctorChoiceProps> = ({
  bookingData,
  setBookingData,
  onNext,
  onPrevious,
}) => {
  const isValid = !!bookingData.doctorID

  const psychiatrists = bookingDoctors.filter(isPsychiatrist)
  const psychologists = bookingDoctors.filter(
    (doctor) => !isPsychiatrist(doctor),
  )

  const selectDoctor = (doctor: BookingDoctor) => {
    setBookingData((prev) => ({
      ...prev,
      doctorID: doctor.id,
      doctorName: doctor.name,
      doctorSpecialty: doctor.specialty,
      bookingDate: undefined,
      slot: '',
    }))
  }

  const renderDoctorCard = (doctor: BookingDoctor, index: number) => {
    const selected = bookingData.doctorID === doctor.id

    return (
      <div key={doctor.id} className={clinicianCardWidthClass}>
        <Card
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          onClick={() => selectDoctor(doctor)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return

            event.preventDefault()
            selectDoctor(doctor)
          }}
          className={`relative aspect-square w-full cursor-pointer overflow-hidden transition-all duration-300 hover:-translate-y-1 active:scale-[0.98] ${
            selected ? 'ring-4 ring-blue-500' : 'ring-1 ring-gray-200'
          }`}
        >
          <Image
            src={doctor.image}
            alt={doctor.name}
            fill
            className="object-cover"
            quality={90}
            sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 50vw"
            priority={index === 0}
            unoptimized
          />

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 via-black/30 to-transparent sm:h-24" />

          {doctor.badge && (
            <span className="absolute right-1.5 top-1.5 z-10 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] leading-tight text-white sm:right-2 sm:top-2 sm:px-2 sm:py-1 sm:text-xs">
              {doctor.badge}
            </span>
          )}

          <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center px-2 py-2 text-center sm:px-4 sm:py-3">
            <h4 className="text-xs font-semibold leading-tight text-white drop-shadow sm:text-sm md:text-base">
              {doctor.name}
            </h4>

            <div className="mt-1 flex max-w-full items-center gap-1 text-[10px] text-white/90 drop-shadow sm:text-xs">
              <Languages className="h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
              <span className="truncate">
                {doctor.languagesKnown.join(', ')}
              </span>
            </div>
          </div>

          {selected && (
            <CheckCircle className="absolute left-1.5 top-1.5 z-10 h-5 w-5 text-blue-300 drop-shadow sm:left-2 sm:top-2 sm:h-6 sm:w-6" />
          )}
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 sm:gap-8">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Choose your clinician
        </h2>
        <p className="mt-2 text-sm text-gray-600 sm:text-base">
          Select the clinician you’d like to book with.
        </p>
      </div>

      <div className="flex flex-col gap-8">
        {psychiatrists.length > 0 && (
          <section className="flex flex-col gap-3">
            <div className="text-center">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Psychiatrist
              </h3>
            </div>

            <div className={clinicianListClass}>
              {psychiatrists.map((doctor, index) =>
                renderDoctorCard(doctor, index),
              )}
            </div>
          </section>
        )}

        {psychologists.length > 0 && (
          <section className="flex flex-col gap-3">
            <div className="text-center">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Psychologists
              </h3>
            </div>

            <div className={clinicianListClass}>
              {psychologists.map((doctor, index) =>
                renderDoctorCard(doctor, index + psychiatrists.length),
              )}
            </div>
          </section>
        )}
      </div>

      <StepActions
        onPrevious={onPrevious}
        onNext={onNext}
        nextDisabled={!isValid}
        previousLabel="Back"
        nextLabel="Show slots"
      />
    </div>
  )
}

export default StepDoctorChoice
